import React from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

interface CitationTooltipProps {
  visible: boolean;
  title: string;
  url: string;
  content: string;
  x: number;
  y: number;
  theme?: 'light' | 'dark';
  backgroundColor?: string;
  textColor?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  position?: 'top' | 'bottom';
  horizontalPosition?: 'left' | 'center' | 'right';
  sourceType?: 'website' | 'document' | 'youtube' | 'phone' | 'email' | 'social';
}

// Function to truncate content by word count
const truncateByWords = (text: string, maxWords: number = 50): { text: string; isTruncated: boolean } => {
  if (!text) return { text: '', isTruncated: false };
  
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) {
    return { text, isTruncated: false };
  }
  
  const truncated = words.slice(0, maxWords).join(' ') + '...';
  return { text: truncated, isTruncated: true };
};

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Custom tooltip content renderer for citations
const renderTooltipContent = (content: string, theme: 'light' | 'dark' = 'light', maxWords?: number): string => {
  if (!content) return '';
  
  // Theme-based colors
  const textColor = theme === 'dark' ? '#d1d5db' : '#6b7280';
  const linkColor = theme === 'dark' ? '#60a5fa' : '#3b82f6';
  
  // Truncate content if maxWords is specified
  let processedContent = content;
  if (maxWords) {
    const { text, isTruncated } = truncateByWords(content, maxWords);
    processedContent = text;
    if (isTruncated) {
      processedContent += ' *[View more in source]*';
    }
  }
  
  // Convert markdown to HTML using marked
  let html = marked.parse(processedContent) as string;
  
  // Count images and keep only the first one
  const imgMatches = html.match(/<img[^>]*>/g);
  if (imgMatches && imgMatches.length > 1) {
    // Keep only the first image and add a count indicator
    let imageCount = 0;
    html = html.replace(/<img[^>]*>/g, (match) => {
      imageCount++;
      if (imageCount === 1) {
        return match; // Keep the first image
      } else if (imageCount === 2) {
        // Replace second image with count indicator
        return `<span style="color: ${textColor}; font-size: 11px; font-style: italic;"> (+${imgMatches.length - 1} more images)</span>`;
      } else {
        return ''; // Remove subsequent images
      }
    });
  }
  
  // Sanitize HTML to prevent XSS
  html = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'img', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'style', 'class'],
  });
  
  // Post-process HTML to add styling and ensure links open in new tab
  html = html
    // Add target="_blank" to all links
    .replace(/<a\s+href=/g, '<a target="_blank" rel="noopener noreferrer" href=')
    // Style links
    .replace(/<a\s/g, `<a style="color: ${linkColor}; text-decoration: underline;" `)
    // Style images - make them clickable and reasonable size
    .replace(/<img\s+([^>]*src=["']([^"']+)["'][^>]*)>/g, (match, attrs, src) => {
      return `<a href="${src}" target="_blank" rel="noopener noreferrer" style="display: inline-block;">
        <img ${attrs} style="max-width: 200px; max-height: 150px; border-radius: 6px; margin: 8px 0; cursor: pointer; display: block;" />
      </a>`;
    })
    // Remove headers (h1-h6) as they're too large for tooltips
    .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/g, '<p><strong>$1</strong></p>');
  
  // Wrap in a div with proper styling
  const styledHtml = `<div style="line-height: 1.5; color: ${textColor}; font-size: 12px; font-weight: normal;">${html}</div>`;
  
  return styledHtml;
};

const CitationTooltip: React.FC<CitationTooltipProps> = ({
  visible,
  title,
  url,
  content,
  x,
  y,
  theme = 'light',
  backgroundColor,
  textColor,
  onMouseEnter,
  onMouseLeave,
  position = 'bottom',
  horizontalPosition = 'center',
  sourceType
}) => {
  if (!visible) return null;
  
  // Clean document title - remove UUID prefix if present
  let displayTitle = title;
  let pageReference = '';
  
  if (sourceType === 'document') {
    // Remove UUID prefix (format: uuid_filename)
    const underscoreIndex = title.indexOf('_');
    if (underscoreIndex > 0 && underscoreIndex < 40) { // UUIDs are ~36 chars
      displayTitle = title.substring(underscoreIndex + 1);
    }
    
    // Extract page reference from content if available
    const pageMatch = content.match(/##\s*Page\s+(\d+)/);
    if (pageMatch) {
      pageReference = ` - Page ${pageMatch[1]}`;
    }
  }

  // Theme-based styles with custom color overrides
  const defaultStyles = theme === 'dark' ? {
    backgroundColor: 'rgba(31, 41, 55, 0.95)',
    color: '#e5e7eb',
    border: '1px solid #374151',
    titleColor: '#f3f4f6',
    linkColor: '#60a5fa',
    linkHoverBg: '#374151',
    borderBottom: '1px solid #4b5563',
    arrowColor: 'rgba(31, 41, 55, 0.95)'
  } : {
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #e5e7eb',
    titleColor: '#111827',
    linkColor: '#3b82f6',
    linkHoverBg: '#f3f4f6',
    borderBottom: '1px solid #f3f4f6',
    arrowColor: 'white'
  };

  // Apply custom colors if provided
  const themeStyles = {
    ...defaultStyles,
    backgroundColor: backgroundColor ? `${backgroundColor} !important` : defaultStyles.backgroundColor,
    color: textColor ? `${textColor} !important` : defaultStyles.color,
    titleColor: textColor ? `${textColor} !important` : defaultStyles.titleColor,
    arrowColor: backgroundColor ? `${backgroundColor} !important` : defaultStyles.arrowColor
  };

  // Position adjustments based on position prop
  const isTop = position === 'top';
  
  // Calculate horizontal transform based on horizontalPosition
  const getHorizontalTransform = () => {
    switch (horizontalPosition) {
      case 'left':
        return 'translateX(0)'; // Align to left edge of marker
      case 'right':
        return 'translateX(-100%)'; // Align to right edge of marker
      case 'center':
      default:
        return 'translateX(-50%)'; // Center on marker
    }
  };
  
  const horizontalTransform = getHorizontalTransform();
  const verticalTransform = isTop ? 'translateY(-100%)' : 'translateY(0)';
  const fullTransform = `${horizontalTransform} ${verticalTransform}`;
  
  // Adjust bridge position based on horizontal alignment
  const bridgeLeft = horizontalPosition === 'left' ? x : 
                     horizontalPosition === 'right' ? x - 40 : 
                     x - 20;
  
  const bridgeStyle = isTop ? {
    position: 'fixed' as const,
    left: bridgeLeft,
    top: y,
    width: '40px',
    height: '20px',
    zIndex: 99998,
    pointerEvents: 'auto' as const
  } : {
    position: 'fixed' as const,
    left: bridgeLeft,
    top: y - 20,
    width: '40px',
    height: '20px',
    zIndex: 99998,
    pointerEvents: 'auto' as const
  };

  const tooltipStyle = isTop ? {
    position: 'fixed' as const,
    left: x,
    top: y,
    transform: fullTransform,
    backgroundColor: themeStyles.backgroundColor,
    color: themeStyles.color,
    padding: '12px',
    paddingBottom: '16px',
    borderRadius: '6px',
    fontSize: '12px',
    maxWidth: '320px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12), 0 3px 5px rgba(0, 0, 0, 0.08)',
    zIndex: 99999,
    pointerEvents: 'auto' as const,
    lineHeight: '1.4',
    border: themeStyles.border,
    display: 'block',
    visibility: 'visible' as const,
    opacity: 1
  } : {
    position: 'fixed' as const,
    left: x,
    top: y + 20,
    transform: fullTransform,
    backgroundColor: themeStyles.backgroundColor,
    color: themeStyles.color,
    padding: '12px',
    paddingTop: '16px',
    borderRadius: '6px',
    fontSize: '12px',
    maxWidth: '320px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12), 0 3px 5px rgba(0, 0, 0, 0.08)',
    zIndex: 99999,
    pointerEvents: 'auto' as const,
    lineHeight: '1.4',
    border: themeStyles.border,
    display: 'block',
    visibility: 'visible' as const,
    opacity: 1
  };

  return (
    <>
      {/* Invisible bridge between marker and tooltip */}
      <div
        style={bridgeStyle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
      
      <div
        className="citation-tooltip-portal"
        style={tooltipStyle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Title and Link */}
        <div style={{ 
          marginBottom: '10px', 
          paddingBottom: '6px', 
          borderBottom: themeStyles.borderBottom,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{ 
            fontWeight: '600', 
            color: themeStyles.titleColor,
            flex: 1,
            fontSize: '13px'
          }}>
            {displayTitle}{pageReference}
          </div>
          {url && sourceType !== 'document' && (
            <a 
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: themeStyles.linkColor,
                textDecoration: 'none',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '3px',
                borderRadius: '3px',
                pointerEvents: 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeStyles.linkHoverBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              🔗
            </a>
          )}
          {sourceType === 'document' && (
            <span style={{
              color: themeStyles.linkColor,
              fontSize: '11px',
              padding: '3px'
            }}>
              📄
            </span>
          )}
        </div>
        
        {/* Content - Full content with scrolling, no truncation */}
        <div 
          className="tooltip-content"
          style={{
            color: themeStyles.color,
            maxHeight: '200px', // Reduced height for better UX
            overflowY: 'auto',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
          dangerouslySetInnerHTML={{
            __html: renderTooltipContent(content, theme, 50) // Truncate to 50 words
          }}
        />
        
        {/* Arrow positioning based on position prop */}
        {isTop ? (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: horizontalPosition === 'left' ? '20px' : 
                    horizontalPosition === 'right' ? 'calc(100% - 20px)' : 
                    '50%',
              transform: horizontalPosition === 'center' ? 'translateX(-50%)' : 'none',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${themeStyles.arrowColor}`
            }}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: horizontalPosition === 'left' ? '20px' : 
                    horizontalPosition === 'right' ? 'calc(100% - 20px)' : 
                    '50%',
              transform: horizontalPosition === 'center' ? 'translateX(-50%)' : 'none',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: `6px solid ${themeStyles.arrowColor}`
            }}
          />
        )}
      </div>
    </>
  );
};

export default CitationTooltip;